import { PageHeader } from '@/components/PageHeader';
import { NAV_ITEMS, WORK_GROUPS } from '@/app/navigation';
import { useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ActionsView } from '@/features/orders/ActionsView';
import { BalanceView } from '@/features/orders/BalanceView';
import { DiscrepanciesView } from '@/features/orders/DiscrepanciesView';
import { DocumentsView } from '@/features/orders/DocumentsView';
import { DutiesView } from '@/features/orders/DutiesView';
import { FollowUpsView } from '@/features/orders/FollowUpsView';
import { MoveView } from '@/features/orders/MoveView';
import { PriceCheckView } from '@/features/orders/PriceCheckView';
import { ReleasesView } from '@/features/orders/ReleasesView';
import { SetupView } from '@/features/orders/SetupView';
import { ToOrderView } from '@/features/orders/ToOrderView';
import { useOrdersDataStore } from '@/stores/ordersDataStore';
import { ordersScrollScope, useOrdersViewStore } from '@/stores/ordersViewStore';
import { useScrollRestoration } from '@/lib/scrollRestoration';
import { QUEUE_CARD_CLASS } from '@/features/orders/workLib';
import { cn } from '@/lib/utils';
import { PageState } from './PageState';

/**
 * Every WORK queue renders through here, so they all share one scroll
 * container, one loading state and one "open the journey" behaviour. In sundae
 * this was a switch inside OrdersPage keyed on a store field; here the route
 * decides, and `openJourney` is a plain navigation to /journeys/:id.
 */
export type WorkViewId =
  | 'actions'
  | 'to_order'
  | 'setup'
  | 'releases'
  | 'decide'
  | 'move'
  | 'documents'
  | 'balance'
  | 'duties'
  | 'prices'
  | 'followups';

export function WorkViewPage({ view }: { view: WorkViewId }) {
  const navigate = useNavigate();
  const location = useLocation();
  const item = NAV_ITEMS.find(item => item.id === view)!;
  const group = WORK_GROUPS.find(group => group.ids.includes(view));
  const { folder } = useParams<{ folder?: string }>();

  const orders = useOrdersDataStore((s) => s.orders);
  const overview = useOrdersDataStore((s) => s.overview);
  const loading = useOrdersDataStore((s) => s.loading);
  const error = useOrdersDataStore((s) => s.error);
  const includeArchived = useOrdersDataStore((s) => s.includeArchived);
  const load = useOrdersDataStore((s) => s.load);

  const sortBy = useOrdersViewStore((s) => s.sortBy);
  const groupBy = useOrdersViewStore((s) => s.groupBy);
  const search = useOrdersViewStore((s) => s.search);

  const onChanged = useCallback(() => {
    void load();
  }, [load]);

  const openJourney = useCallback(
    (orderId: string) => navigate(`/journeys/${encodeURIComponent(orderId)}`, { state: { returnTo: location.pathname + location.search } }),
    [navigate, location.pathname, location.search]
  );

  const scrollViewportRef = useRef<HTMLDivElement | null>(null);
  const ready = Boolean(overview);
  useScrollRestoration({
    scopeKey: ordersScrollScope({
      view,
      stageFilter: 'all',
      includeArchived,
      sortBy,
      groupBy,
      search,
    }),
    viewportRef: scrollViewportRef,
    ready,
  });

  const activeOrders = orders.filter((o) => !o.archivedAt);
  const tasks = overview?.tasks ?? [];

  return (
    <div className="relative flex h-full flex-col overflow-hidden bg-background">
      <PageHeader
        eyebrow={group?.label || 'Work'}
        title={item.label}
        about={item.description}
      />
      <div ref={scrollViewportRef} className="min-h-0 flex-1 overflow-y-auto">
        <PageState loading={loading && !overview} error={error} onRetry={() => void load()} />

        {ready && overview && (
          <div
            data-queue-content=""
            data-queue={view}
            className={cn('mx-8 mb-8 max-md:mx-3 max-md:mb-4', QUEUE_CARD_CLASS)}
          >
            {view === 'actions' && (
              <ActionsView
                actions={overview.actions ?? []}
                onChanged={onChanged}
                onOpenJourney={openJourney}
              />
            )}
            {view === 'to_order' && (
              <ToOrderView
                orders={activeOrders.filter((o) => o.status === 'announced')}
                onChanged={onChanged}
                onOpenJourney={openJourney}
              />
            )}
            {view === 'setup' && (
              <SetupView
                orders={activeOrders.filter(
                  (o) =>
                    (o.needsSetup || o.newSupplierNeeded) &&
                    !['cancelled', 'wont_order', 'closed'].includes(o.status)
                )}
                tasks={tasks}
                onChanged={onChanged}
                onOpenJourney={openJourney}
                initialFolder={folder ?? null}
              />
            )}
            {view === 'releases' && <ReleasesView onOpenJourney={openJourney} />}
            {view === 'decide' && (
              <DiscrepanciesView
                overview={overview}
                onChanged={onChanged}
                onOpenJourney={openJourney}
              />
            )}
            {view === 'move' && <MoveView overview={overview} onOpenJourney={openJourney} />}
            {view === 'documents' && (
              <DocumentsView
                overview={overview}
                tasks={tasks}
                onChanged={onChanged}
                onOpenJourney={openJourney}
              />
            )}
            {view === 'balance' && (
              <BalanceView
                overview={overview}
                tasks={tasks}
                onChanged={onChanged}
                onOpenJourney={openJourney}
              />
            )}
            {view === 'duties' && (
              <DutiesView overview={overview} onChanged={onChanged} onOpenJourney={openJourney} />
            )}
            {view === 'prices' && <PriceCheckView onChanged={onChanged} />}
            {view === 'followups' && (
              <FollowUpsView
                overview={overview}
                orders={activeOrders}
                onChanged={onChanged}
                onOpenJourney={openJourney}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
